import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Container,
  Chip,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  Security as LicenseCheckIcon,
  Work as PSPIcon,
  Language as LanguageIcon,
  Fingerprint as BackgroundCheckIcon,
  Description as ResumeIcon,
} from '@mui/icons-material';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  tags?: string[];
  featured?: boolean;
}

const services: Service[] = [
  {
    id: 'reports',
    title: 'Reports',
    description: 'Create and manage DriverVerified reports for yourself or a team.',
    icon: <ReportsIcon />,
    path: '/services/reports',
  },
  {
    id: 'license-check',
    title: "Driver's License Status Check",
    description: 'Instantly check the validity of any Canadian driver\'s licence.',
    icon: <LicenseCheckIcon />,
    path: '/services/license-check',
  },
  {
    id: 'psp-reports',
    title: 'PSP Report Service',
    description: 'Pre-Employment Screening Program (PSP) Reports provides carriers access to American commercial driver safety records from the Federal Motor Carrier Safety Administration\'s (FMCSA) Motor Carrier Management Information System (MCMIS).',
    icon: <PSPIcon />,
    path: '/services/psp-reports',
  },
  {
    id: 'language-test',
    title: 'English Language Proficiency Test',
    description: 'Our English Language Proficiency Test service is an online test that measures English language ability in listening, speaking, writing, and conversing.',
    icon: <LanguageIcon />,
    path: '/services/language-test',
  },
  {
    id: 'background-checks',
    title: "Driver's Abstracts and Background Checks",
    description: 'Order government documents for commercial drivers and fleets, including Driver\'s Abstracts/Motor Vehicle Abstracts (MVRs), CVORDR, Criminal Record Checks, and Employment Reference.',
    icon: <BackgroundCheckIcon />,
    path: '/services/background-checks',
  },
  {
    id: 'resume',
    title: 'Resume',
    description: 'Customize and download a basic resume that is generated using your DriverVerified Profile.',
    icon: <ResumeIcon />,
    path: '/services/resume',
  },
];

export const ServicesList = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 600,
            background: 'linear-gradient(135deg, #00B4A6 0%, #1976d2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 2
          }}
        >
          Services
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6
          }}
        >
          Professional driver verification and compliance services
        </Typography>
      </Box>

      <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
        {services.map((service, index) => (
          <Card
            key={service.id}
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              p: 3,
              transition: 'all 0.3s ease',
              border: '1px solid #e0e0e0',
              borderRadius: '12px',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                borderColor: '#1976d2',
              },
            }}
            onClick={() => navigate(service.path)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: '16px',
                backgroundColor: '#1976d2',
                color: 'white',
                mr: 3,
                flexShrink: 0,
              }}
            >
              {service.icon}
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: '#1976d2'
                }}
              >
                {service.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {service.description}
              </Typography>
            </Box>

            <Button
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #00B4A6 0%, #00A693 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.2,
                ml: 2,
                minWidth: '120px',
                height: '40px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(0, 180, 166, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #00A693 0%, #009580 100%)',
                  boxShadow: '0 6px 16px rgba(0, 180, 166, 0.4)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Learn More
            </Button>
          </Card>
        ))}
      </Box>
    </Container>
  );
}; 